// Licenças do condomínio (P-15).
//
// A aba era HTML puro, sem estado e sem o `id` do condomínio. "Demais Licenças"
// tinha um botão "Adicionar Licença" que não abria nada e um "Nenhum dado para
// exibição" permanente.
//
// O Habite-se saiu das perguntas: é `tmpl-005` em `alvaras_condominio`. E
// "Demais Licenças" virou o que sempre foi — licença avulsa, sem template no
// catálogo, gravada na mesma tabela das outras. Uma licença fora do catálogo
// continua tendo órgão emissor, vencimento e documento; inventar uma segunda
// tabela para ela só criaria uma segunda tela de renovação.

import { useCallback, useEffect, useState } from 'react';
import {
  excluirAlvara,
  gravarAlvara,
  lerAlvaras,
  lerLicencas,
  gravarLicencas,
  LICENCAS_VAZIAS,
  TEMPLATE_HABITE_SE,
  TEMPLATE_REFORMA,
  type AlvaraRegistro,
  type LicencasDoCondominio,
} from '@/lib/condominioRepo';
import { useJanelaDeAlerta } from '@/lib/notificacoes';
import { ateOuIgual, MENSAGEM_DATA } from '@/lib/datas';
import DocumentoDoAlvara, {
  SeloDeSituacao,
} from '@/pages/condominios/detail/components/DocumentoDoAlvara';

const CLASSE_CAMPO =
  'w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm';

const formatData = (iso: string | null) => {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

function SimNao({
  id,
  rotulo,
  valor,
  onChange,
}: {
  id: string;
  rotulo: string;
  valor: boolean | null;
  onChange: (v: boolean | null) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-foreground-700 mb-1.5">
        {rotulo}
      </label>
      <select
        id={id}
        value={valor === null ? '' : valor ? 'sim' : 'nao'}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'sim')}
        className={CLASSE_CAMPO}
      >
        <option value="">Selecione...</option>
        <option value="sim">Sim</option>
        <option value="nao">Não</option>
      </select>
    </div>
  );
}

interface FormularioLicenca {
  id?: string;
  nome: string;
  orgaoEmissor: string;
  numeroDocumento: string;
  dataEmissao: string;
  dataVencimento: string;
}

const FORMULARIO_VAZIO: FormularioLicenca = {
  nome: '',
  orgaoEmissor: '',
  numeroDocumento: '',
  dataEmissao: '',
  dataVencimento: '',
};

export default function LicencasTab({ condoId }: { condoId: string }) {
  const janelaAlvara = useJanelaDeAlerta('alvara');

  const [dados, setDados] = useState<LicencasDoCondominio>(LICENCAS_VAZIAS);
  const [habiteSe, setHabiteSe] = useState<AlvaraRegistro | null>(null);
  const [reforma, setReforma] = useState<AlvaraRegistro | null>(null);
  const [avulsas, setAvulsas] = useState<AlvaraRegistro[]>([]);

  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{ texto: string; tipo: 'ok' | 'erro' } | null>(null);

  const [formulario, setFormulario] = useState<FormularioLicenca | null>(null);
  const [erroFormulario, setErroFormulario] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState<AlvaraRegistro | null>(null);

  const carregar = useCallback(async () => {
    if (!condoId) return;
    setCarregando(true);
    try {
      const [licencas, alvaras] = await Promise.all([
        lerLicencas(condoId),
        lerAlvaras(condoId, janelaAlvara),
      ]);
      setDados(licencas ?? LICENCAS_VAZIAS);
      setHabiteSe(alvaras.find((a) => a.templateId === TEMPLATE_HABITE_SE) ?? null);
      setReforma(alvaras.find((a) => a.templateId === TEMPLATE_REFORMA) ?? null);
      setAvulsas(alvaras.filter((a) => a.templateId === null));
      setAviso(null);
    } catch (e: any) {
      setAviso({ texto: e?.message || 'Erro ao carregar.', tipo: 'erro' });
    } finally {
      setCarregando(false);
    }
  }, [condoId, janelaAlvara]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const alterar = <K extends keyof LicencasDoCondominio>(
    campo: K,
    valor: LicencasDoCondominio[K],
  ) => {
    setDados((atual) => ({ ...atual, [campo]: valor }));
    setAviso(null);
  };

  const salvar = async () => {
    setSalvando(true);
    setAviso(null);
    try {
      await gravarLicencas(condoId, { ...dados, observacoes: dados.observacoes?.trim() || null });
      setAviso({ texto: 'Licenças salvas.', tipo: 'ok' });
    } catch (e: any) {
      setAviso({ texto: e?.message || 'Não foi possível salvar.', tipo: 'erro' });
    } finally {
      setSalvando(false);
    }
  };

  const salvarLicencaAvulsa = async () => {
    if (!formulario) return;

    if (!formulario.nome.trim()) {
      setErroFormulario('Informe o nome da licença.');
      return;
    }
    if (
      formulario.dataEmissao &&
      formulario.dataVencimento &&
      !ateOuIgual(formulario.dataEmissao, formulario.dataVencimento)
    ) {
      setErroFormulario(MENSAGEM_DATA.fimAntesDoInicio);
      return;
    }

    setErroFormulario(null);
    try {
      await gravarAlvara({
        id: formulario.id,
        condominioId: condoId,
        templateId: null,
        nome: formulario.nome.trim(),
        categoria: 'Outras licenças',
        // Sem vencimento não há como afirmar vigência — o banco recusaria
        // `vigente` sem data (migration 0029), e a tela combina com ele.
        status: formulario.dataVencimento ? 'vigente' : 'pendente',
        orgaoEmissor: formulario.orgaoEmissor.trim(),
        dataEmissao: formulario.dataEmissao || null,
        dataVencimento: formulario.dataVencimento || null,
        numeroDocumento: formulario.numeroDocumento.trim() || null,
        motivoNaoAplica: null,
        anexos: [],
      });
      setFormulario(null);
      await carregar();
    } catch (e: any) {
      setErroFormulario(e?.message || 'Não foi possível salvar a licença.');
    }
  };

  const confirmarExclusao = async () => {
    if (!excluindo) return;
    try {
      await excluirAlvara(excluindo.id);
      setExcluindo(null);
      await carregar();
    } catch (e: any) {
      setAviso({ texto: e?.message || 'Não foi possível excluir.', tipo: 'erro' });
      setExcluindo(null);
    }
  };

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex items-center gap-3 text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-xl"></i>
          <span className="text-sm">Carregando licenças...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Prefeitura */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4">
          Status junto à Prefeitura
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <SimNao
            id="lic-regular"
            rotulo="Regular junto à prefeitura?"
            valor={dados.regular_prefeitura}
            onChange={(v) => alterar('regular_prefeitura', v)}
          />
        </div>
        <DocumentoDoAlvara
          titulo="Habite-se (certificado de conclusão)"
          explicacao="O Habite-se é um alvará: número, emissão e documento digitalizado ficam no registro dele."
          registro={habiteSe}
          condoId={condoId}
        />
      </div>

      {/* Reformas */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4">Reformas</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SimNao
            id="lic-houve-reforma"
            rotulo="Houve reformas com acréscimo de área após o Habite-se?"
            valor={dados.houve_reforma_acrescimo}
            onChange={(v) => alterar('houve_reforma_acrescimo', v)}
          />
          <SimNao
            id="lic-havera-reforma"
            rotulo="Haverá nova reforma com acréscimo de área?"
            valor={dados.havera_reforma_acrescimo}
            onChange={(v) => alterar('havera_reforma_acrescimo', v)}
          />
        </div>

        {(dados.houve_reforma_acrescimo || dados.havera_reforma_acrescimo) && (
          <div className="mt-4">
            <DocumentoDoAlvara
              titulo="Alvará de Construção / Reforma"
              explicacao="Acréscimo de área exige alvará de reforma. O registro fica na aba Alvarás."
              registro={reforma}
              condoId={condoId}
            />
          </div>
        )}
      </div>

      {/* Demais licenças */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg">
        <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-foreground-900">Demais Licenças</h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              Licenças fora do catálogo de alvarás. Aparecem na aba Alvarás junto das outras.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setFormulario({ ...FORMULARIO_VAZIO });
              setErroFormulario(null);
            }}
            className="whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
          >
            <i className="ri-add-line"></i>Adicionar Licença
          </button>
        </div>

        {avulsas.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-foreground-500">Nenhuma licença avulsa cadastrada.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200/70 bg-background-100/50">
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3">
                    Licença
                  </th>
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 hidden sm:table-cell">
                    Órgão
                  </th>
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Vencimento
                  </th>
                  <th className="text-center text-xs font-semibold text-foreground-500 px-4 py-3">
                    Situação
                  </th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/50">
                {avulsas.map((lic) => (
                  <tr key={lic.id} className="hover:bg-background-100/30 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-foreground-800">{lic.nome}</p>
                      {lic.numeroDocumento && (
                        <p className="text-xs text-foreground-500 font-mono">
                          {lic.numeroDocumento}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="text-sm text-foreground-600">{lic.orgaoEmissor || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-foreground-700 whitespace-nowrap">
                        {formatData(lic.dataVencimento)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <SeloDeSituacao situacao={lic.situacao} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            setFormulario({
                              id: lic.id,
                              nome: lic.nome,
                              orgaoEmissor: lic.orgaoEmissor,
                              numeroDocumento: lic.numeroDocumento ?? '',
                              dataEmissao: lic.dataEmissao ?? '',
                              dataVencimento: lic.dataVencimento ?? '',
                            });
                            setErroFormulario(null);
                          }}
                          title="Editar licença"
                          className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-primary-600 transition-colors"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          type="button"
                          onClick={() => setExcluindo(lic)}
                          title="Excluir licença"
                          className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Observações */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <label htmlFor="lic-obs" className="block text-sm font-semibold text-foreground-900 mb-3">
          Observações
        </label>
        <textarea
          id="lic-obs"
          rows={3}
          maxLength={2000}
          value={dados.observacoes ?? ''}
          onChange={(e) => alterar('observacoes', e.target.value)}
          className={`${CLASSE_CAMPO} resize-y`}
        />
      </div>

      <div className="flex items-center justify-end gap-3">
        {aviso && (
          <span
            className={`text-sm flex items-center gap-1.5 ${aviso.tipo === 'ok' ? 'text-green-700' : 'text-red-700'}`}
          >
            <i className={aviso.tipo === 'ok' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
            {aviso.texto}
          </span>
        )}
        <button
          type="button"
          onClick={salvar}
          disabled={salvando}
          className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {salvando ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      {/* Formulário da licença avulsa */}
      {formulario && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setFormulario(null)}
            aria-hidden="true"
          ></div>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="titulo-licenca"
            className="relative bg-background-50 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
              <h3 id="titulo-licenca" className="text-base font-semibold text-foreground-900">
                {formulario.id ? 'Editar licença' : 'Nova licença'}
              </h3>
              <button
                type="button"
                onClick={() => setFormulario(null)}
                aria-label="Fechar"
                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="p-5 flex flex-col gap-4">
              <div>
                <label
                  htmlFor="lic-nome"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  Nome da licença
                </label>
                <input
                  id="lic-nome"
                  type="text"
                  value={formulario.nome}
                  onChange={(e) => setFormulario({ ...formulario, nome: e.target.value })}
                  placeholder="Licença de publicidade, autorização de evento..."
                  className={CLASSE_CAMPO}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="lic-orgao"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Órgão emissor
                  </label>
                  <input
                    id="lic-orgao"
                    type="text"
                    value={formulario.orgaoEmissor}
                    onChange={(e) => setFormulario({ ...formulario, orgaoEmissor: e.target.value })}
                    className={CLASSE_CAMPO}
                  />
                </div>
                <div>
                  <label
                    htmlFor="lic-numero"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Número do documento
                  </label>
                  <input
                    id="lic-numero"
                    type="text"
                    value={formulario.numeroDocumento}
                    onChange={(e) =>
                      setFormulario({ ...formulario, numeroDocumento: e.target.value })
                    }
                    className={CLASSE_CAMPO}
                  />
                </div>
                <div>
                  <label
                    htmlFor="lic-emissao"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Emissão
                  </label>
                  <input
                    id="lic-emissao"
                    type="date"
                    value={formulario.dataEmissao}
                    onChange={(e) => setFormulario({ ...formulario, dataEmissao: e.target.value })}
                    className={CLASSE_CAMPO}
                  />
                </div>
                <div>
                  <label
                    htmlFor="lic-vencimento"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Vencimento
                  </label>
                  <input
                    id="lic-vencimento"
                    type="date"
                    value={formulario.dataVencimento}
                    onChange={(e) =>
                      setFormulario({ ...formulario, dataVencimento: e.target.value })
                    }
                    className={CLASSE_CAMPO}
                  />
                </div>
              </div>

              {erroFormulario && (
                <p className="text-sm text-red-700 flex items-center gap-1.5">
                  <i className="ri-error-warning-line"></i>
                  {erroFormulario}
                </p>
              )}
            </div>

            <div className="px-5 py-4 border-t border-background-200/70 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setFormulario(null)}
                className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={salvarLicencaAvulsa}
                className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
              >
                Salvar licença
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmação de exclusão */}
      {excluindo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setExcluindo(null)}
            aria-hidden="true"
          ></div>
          <div
            role="dialog"
            aria-modal="true"
            className="relative bg-background-50 rounded-lg shadow-xl w-full max-w-md p-5"
          >
            <h3 className="text-base font-semibold text-foreground-900 mb-2">Excluir licença</h3>
            <p className="text-sm text-foreground-600 mb-5">
              Excluir <span className="font-medium">{excluindo.nome}</span>? O registro sai da
              listagem, mas continua no banco marcado como excluído.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setExcluindo(null)}
                className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmarExclusao}
                className="px-4 py-2 rounded-md bg-red-600 text-background-50 text-sm font-semibold hover:bg-red-700 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
